import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateRange } from 'react-date-range';
import moment from 'moment';
import Layouts from '../../layouts/Layouts';
import { getAuthorizationHeader, http } from '../../config/http';
import {
  IconCalendar,
  IconInbox,
  IconSend,
  IconChevronDown,
  IconEye,
  IconX,
  IconSearch,
  IconChartPie,
  IconListDetails,
  IconBuildingStore,
} from '@tabler/icons-react';
import { DinamicTitle } from '../../utils/DinamicTitle';
import 'react-date-range/dist/styles.css'; 
import 'react-date-range/dist/theme/default.css';

const CategoryAccordionItem = ({ catName, catData, isForceOpen, onSelectKarat }) => {
  const [isOpen, setIsOpen] = useState(false);
  const details = catData?.details || [];
  const hasDetails = details.length > 0;

  const activeOpen = isForceOpen !== undefined ? isForceOpen : isOpen;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-800 shadow-2xs overflow-hidden transition-all duration-200">
      <button
        type="button"
        onClick={() => setIsOpen(!activeOpen)}
        className="w-full flex justify-between items-center text-sm px-3.5 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors text-left group cursor-pointer select-none"
        title="Klik untuk membuka/menutup rincian per karat"
      >
        <div className="flex items-center gap-2.5 min-w-0 pr-2">
          <IconChevronDown
            size={16}
            className={`text-slate-400 dark:text-slate-500 transition-transform duration-200 shrink-0 group-hover:text-amber-500 ${
              activeOpen ? 'rotate-180 text-amber-500 dark:text-amber-400' : ''
            }`}
          />
          <span className="font-bold text-slate-800 dark:text-slate-100 text-sm truncate">
            {catName}
          </span>
          {hasDetails && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200/70 dark:border-amber-800/60 shrink-0">
              {details.length} Karat
            </span>
          )}
        </div>

        <div className="flex items-center gap-2.5 text-xs shrink-0">
          <span className="bg-slate-100 dark:bg-slate-700/60 px-2 py-1 rounded text-slate-700 dark:text-slate-300 font-semibold">
            {catData.pcs} pcs
          </span>
          <span className="bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/50 px-2 py-1 rounded font-mono font-bold">
            {Number(catData.berat || 0).toFixed(3)} gr
          </span>
        </div>
      </button>

      {/* Expanded Karat Details */}
      {activeOpen && (
        <div className="bg-slate-50/80 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-700/80 p-3 space-y-2">
          {hasDetails ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {details.map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => onSelectKarat && onSelectKarat(catName, item.karat)}
                  className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 shadow-2xs hover:border-amber-300 dark:hover:border-amber-500 transition-colors"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    <span className="font-bold text-xs text-amber-800 dark:text-amber-400">
                      {item.karat?.toString().includes('K') || item.karat?.toString().includes('k')
                        ? item.karat
                        : `${item.karat} K`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 text-xs">
                    <span className="text-slate-600 dark:text-slate-300 font-medium">
                      <strong className="text-slate-800 dark:text-slate-100 font-bold">{item.pcs}</strong> pcs
                    </span>
                    <span className="text-slate-400">/</span>
                    <span className="font-mono font-bold text-slate-700 dark:text-slate-200">
                      {Number(item.berat || 0).toFixed(3)} gr
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-2 text-xs text-slate-400 italic">
              Tidak ada rincian karat untuk kategori ini
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RenderSummaryCard = ({ title, summary, type, onOpenDetail }) => {
  const isMasuk = type === 'masuk';
  const [allExpanded, setAllExpanded] = useState(false);
  const categoriesCount = Object.keys(summary?.categories || {}).length;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col transition-all hover:shadow-md">
      {/* Card Header */}
      <div
        className={`p-4 sm:p-5 border-b flex items-center justify-between gap-3 ${
          isMasuk
            ? 'bg-gradient-to-r from-emerald-50 to-teal-50/50 dark:from-emerald-950/30 dark:to-slate-800 border-emerald-100 dark:border-emerald-800/30'
            : 'bg-gradient-to-r from-rose-50 to-orange-50/50 dark:from-rose-950/30 dark:to-slate-800 border-rose-100 dark:border-rose-800/30'
        }`}
      >
        <div className="flex items-center gap-3.5 min-w-0">
          <div
            className={`p-2.5 sm:p-3 rounded-xl shadow-xs shrink-0 ${
              isMasuk
                ? 'bg-emerald-500 text-white'
                : 'bg-rose-500 text-white'
            }`}
          >
            {isMasuk ? <IconInbox size={24} stroke={2} /> : <IconSend size={24} stroke={2} />}
          </div>
          <div className="truncate">
            <h3
              className={`font-bold text-base sm:text-lg leading-tight ${
                isMasuk ? 'text-emerald-800 dark:text-emerald-400' : 'text-rose-800 dark:text-rose-400'
              }`}
            >
              {title}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
              {isMasuk ? 'Data barang diinput pada periode ini' : 'Data barang dikirim ke pusat periode ini'}
            </p>
          </div>
        </div>

        {/* Action Button: Lihat Detail */}
        <button
          type="button"
          onClick={() => onOpenDetail(type)}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer ${
            isMasuk
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20'
              : 'bg-rose-600 hover:bg-rose-700 text-white shadow-rose-600/20'
          }`}
          title="Lihat Detail & Daftar Barang"
        >
          <IconEye size={15} />
          <span className="hidden sm:inline">Detail</span>
        </button>
      </div>

      {/* Card Body */}
      <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
        {/* Top KPI Stats (3 Cards) */}
        <div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
            <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-100 dark:border-slate-700/80 text-center">
              <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-0.5">
                Total PCS
              </p>
              <p className="text-lg sm:text-xl font-black text-slate-800 dark:text-slate-100">
                {summary?.totalBarang || 0}
              </p>
            </div>
            <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-100 dark:border-slate-700/80 text-center">
              <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-0.5">
                Total Berat
              </p>
              <p className="text-lg sm:text-xl font-black text-amber-600 dark:text-amber-400 font-mono">
                {(summary?.totalBerat || 0).toFixed(3)}{' '}
                <span className="text-[11px] font-normal text-slate-400">gr</span>
              </p>
            </div>
            <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-100 dark:border-slate-700/80 text-center">
              <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-0.5">
                Kategori
              </p>
              <p className="text-lg sm:text-xl font-black text-slate-800 dark:text-slate-100">
                {categoriesCount}
              </p>
            </div>
          </div>

          {/* Section: Ringkasan Item Berdasarkan Kategori & Karat */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-700/60">
              <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                <IconChartPie size={15} className="text-amber-500" />
                <span>Ringkasan Item Berdasarkan Kategori & Karat</span>
              </h4>
              {categoriesCount > 0 && (
                <button
                  type="button"
                  onClick={() => setAllExpanded(!allExpanded)}
                  className="text-[11px] font-semibold text-amber-600 hover:text-amber-700 dark:text-amber-400 hover:underline cursor-pointer"
                >
                  {allExpanded ? 'Tutup Semua' : 'Buka Semua'}
                </button>
              )}
            </div>

            {!summary?.categories || categoriesCount === 0 ? (
              <div className="text-center py-8 text-xs text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                Tidak ada data pada periode ini
              </div>
            ) : (
              <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                {Object.entries(summary.categories).map(([catName, catData], idx) => (
                  <CategoryAccordionItem
                    key={catName || idx}
                    catName={catName}
                    catData={catData}
                    isForceOpen={allExpanded ? true : undefined}
                    onSelectKarat={() => onOpenDetail(type)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Card Footer Action */}
        <div className="pt-4 mt-4 border-t border-slate-100 dark:border-slate-700/80">
          <button
            type="button"
            onClick={() => onOpenDetail(type)}
            className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
              isMasuk
                ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/60 dark:text-emerald-300 border border-emerald-200/70 dark:border-emerald-800/50'
                : 'bg-rose-50 hover:bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 dark:text-rose-300 border border-rose-200/70 dark:border-rose-800/50'
            }`}
          >
            <IconListDetails size={16} />
            <span>Lihat Detail Lengkap & Rincian Barang</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Detail Modal Component
const DetailLaporanModal = ({ isOpen, onClose, modalData, type, startDate, endDate }) => {
  const [modalTab, setModalTab] = useState('ringkasan'); // 'ringkasan' | 'items'
  const [modalSearch, setModalSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const isMasuk = type === 'masuk';
  const summary = modalData || {};
  const categories = summary.categories || {};
  const items = useMemo(() => modalData?.items || [], [modalData]);

  const filteredItems = useMemo(() => {
    if (!modalSearch.trim()) return items;
    const q = modalSearch.toLowerCase();
    return items.filter((it) => {
      const barcode = it.barcode?.toLowerCase() || '';
      const kode = it.kode_item?.toLowerCase() || '';
      const jenis = it.jenis_kategori?.nama_jenis?.toLowerCase() || '';
      const kat = it.jenis_kategori?.kategori?.nama_kategori?.toLowerCase() || '';
      const karat = String(it.karat || '').toLowerCase();
      return (
        barcode.includes(q) ||
        kode.includes(q) ||
        jenis.includes(q) ||
        kat.includes(q) ||
        karat.includes(q)
      );
    });
  }, [items, modalSearch]);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage) || 1;
  const paginatedItems = useMemo(() => {
    return filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredItems, currentPage, itemsPerPage]);

  if (!isOpen) return null;

  const totalPcs = summary.totalBarang || 0;
  const totalBerat = Number(summary.totalBerat || 0);
  const totalCategories = Object.keys(categories).length;
  const avgBerat = totalPcs > 0 ? (totalBerat / totalPcs).toFixed(3) : '0.000';

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-5 animate-[fadeIn_0.2s_ease-out]"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          className={`p-4 sm:p-5 border-b flex items-center justify-between gap-3 ${
            isMasuk
              ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white'
              : 'bg-gradient-to-r from-rose-500 to-red-600 text-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-xs rounded-xl text-white">
              {isMasuk ? <IconInbox size={24} /> : <IconSend size={24} />}
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black leading-tight">
                {isMasuk ? 'Detail Barang Masuk' : 'Detail Barang Keluar (Pusat)'}
              </h2>
              <p className="text-xs text-white/80 mt-0.5 font-medium">
                Periode: {moment(startDate).format('DD MMM YYYY')} - {moment(endDate).format('DD MMM YYYY')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors cursor-pointer text-white"
            title="Tutup Modal"
          >
            <IconX size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-5 flex-1">
          {/* Top KPI Cards (4 Cards) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
            <div className="bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-200/80 dark:border-slate-700/80">
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">
                Total Barang
              </span>
              <div className="text-lg font-black text-slate-800 dark:text-slate-100 mt-0.5">
                {totalPcs} <span className="text-xs font-semibold text-slate-500">pcs</span>
              </div>
            </div>
            <div className="bg-amber-50/60 dark:bg-amber-950/20 p-3 rounded-xl border border-amber-200/70 dark:border-amber-900/40">
              <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider block">
                Total Berat
              </span>
              <div className="text-lg font-black text-amber-600 dark:text-amber-400 font-mono mt-0.5">
                {totalBerat.toFixed(3)}{' '}
                <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-200/80 dark:border-slate-700/80">
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">
                Total Kategori
              </span>
              <div className="text-lg font-black text-slate-800 dark:text-slate-100 mt-0.5">
                {totalCategories} <span className="text-xs font-semibold text-slate-500">Kategori</span>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-200/80 dark:border-slate-700/80">
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">
                Rata-rata Berat
              </span>
              <div className="text-lg font-black text-slate-800 dark:text-slate-100 font-mono mt-0.5">
                {avgBerat}{' '}
                <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex border-b border-slate-200 dark:border-slate-700 gap-4">
            <button
              type="button"
              onClick={() => setModalTab('ringkasan')}
              className={`pb-3 px-2 font-bold text-xs sm:text-sm flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
                modalTab === 'ringkasan'
                  ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <IconChartPie size={17} />
              <span>Ringkasan Kategori & Karat</span>
            </button>
            <button
              type="button"
              onClick={() => setModalTab('items')}
              className={`pb-3 px-2 font-bold text-xs sm:text-sm flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
                modalTab === 'items'
                  ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <IconListDetails size={17} />
              <span>Daftar Barang (Item)</span>
              {items.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                  {items.length}
                </span>
              )}
            </button>
          </div>

          {/* TAB 1: Ringkasan Kategori & Karat */}
          {modalTab === 'ringkasan' && (
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Ringkasan Item Berdasarkan Kategori & Karat
              </h4>

              {totalCategories === 0 ? (
                <div className="text-center py-12 text-slate-400 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                  Tidak ada data kategori pada periode ini
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  {Object.entries(categories).map(([catName, catData], idx) => {
                    const karatDetails = catData.details || [];
                    return (
                      <div
                        key={idx}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-xl p-4 shadow-xs"
                      >
                        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/60 pb-2.5 mb-3">
                          <h5 className="font-bold text-slate-800 dark:text-slate-100 text-sm">
                            {catName}
                          </h5>
                          <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-400 font-bold px-2.5 py-1 rounded-lg text-xs">
                            {catData.pcs} PCS ({Number(catData.berat || 0).toFixed(3)} gr)
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {karatDetails.map((kData, kIdx) => (
                            <div
                              key={kIdx}
                              className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-3 text-xs"
                            >
                              <span className="font-bold text-amber-700 dark:text-amber-400">
                                {kData.karat}
                              </span>
                              <span className="font-semibold text-slate-700 dark:text-slate-300">
                                {kData.pcs} PCS / {Number(kData.berat || 0).toFixed(3)} gr
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: Daftar Detail Barang (Tabel) */}
          {modalTab === 'items' && (
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <IconSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={16}
                />
                <input
                  type="text"
                  value={modalSearch}
                  onChange={(e) => {
                    setModalSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Cari barcode, kode item, jenis, kategori, karat..."
                  className="w-full pl-9 pr-8 py-2 text-xs sm:text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 dark:focus:ring-amber-900/30 transition-all text-slate-800 dark:text-slate-100"
                />
                {modalSearch && (
                  <button
                    type="button"
                    onClick={() => {
                      setModalSearch('');
                      setCurrentPage(1);
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 rounded-full cursor-pointer"
                  >
                    <IconX size={14} />
                  </button>
                )}
              </div>

              {/* Items Table */}
              <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-2xs">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left text-slate-700 dark:text-slate-300">
                    <thead className="text-[11px] font-bold uppercase bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700 text-slate-500 tracking-wider">
                      <tr>
                        <th className="px-4 py-3">NO.</th>
                        <th className="px-4 py-3">BARCODE / KODE ITEM</th>
                        <th className="px-4 py-3">JENIS & KATEGORI</th>
                        <th className="px-4 py-3">CABANG / TOKO</th>
                        <th className="px-4 py-3 text-center">KARAT</th>
                        <th className="px-4 py-3 text-right">BERAT</th>
                        <th className="px-4 py-3 text-center">TGL INPUT</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                      {paginatedItems.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-10 text-slate-400">
                            {items.length === 0
                              ? 'Daftar item tidak tersedia untuk periode ini.'
                              : 'Tidak ada item yang sesuai dengan kata kunci pencarian.'}
                          </td>
                        </tr>
                      ) : (
                        paginatedItems.map((item, idx) => (
                          <tr
                            key={item.id || idx}
                            className="bg-white dark:bg-slate-900 hover:bg-amber-50/30 dark:hover:bg-slate-800/60 transition-colors"
                          >
                            <td className="px-4 py-3 font-medium text-slate-500">
                              {(currentPage - 1) * itemsPerPage + idx + 1}.
                            </td>
                            <td className="px-4 py-3 font-mono font-bold text-amber-600 dark:text-amber-400">
                              {item.barcode || item.kode_item || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-bold text-slate-800 dark:text-slate-100 block">
                                {item.jenis_kategori?.nama_jenis || '-'}
                              </span>
                              <span className="text-[11px] text-slate-400">
                                {item.jenis_kategori?.kategori?.nama_kategori || 'Tanpa Kategori'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                              <span className="font-semibold text-slate-700 dark:text-slate-300">
                                {item.cabang?.nama_cabang || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/50">
                                {item.karat ? `${item.karat} K` : '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-mono font-bold text-slate-800 dark:text-slate-100">
                              {Number(item.berat || 0).toFixed(3)} gr
                            </td>
                            <td className="px-4 py-3 text-center text-slate-500 text-[11px]">
                              {item.created_at
                                ? moment(item.created_at).format('DD/MM/YYYY, HH:mm')
                                : '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50/50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 text-xs">
                    <span className="text-slate-500">
                      Menampilkan {(currentPage - 1) * itemsPerPage + 1} -{' '}
                      {Math.min(currentPage * itemsPerPage, filteredItems.length)} dari {filteredItems.length} item
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-2.5 py-1 border border-slate-200 dark:border-slate-700 rounded text-slate-600 dark:text-slate-300 disabled:opacity-40 transition-all cursor-pointer"
                      >
                        Prev
                      </button>
                      <span className="px-2 font-bold text-slate-700 dark:text-slate-200">
                        {currentPage} / {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-2.5 py-1 border border-slate-200 dark:border-slate-700 rounded text-slate-600 dark:text-slate-300 disabled:opacity-40 transition-all cursor-pointer"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-100 dark:border-slate-800 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all cursor-pointer"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

const LaporanKeluarMasukOperator = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [summaryMasuk, setSummaryMasuk] = useState({ totalBarang: 0, totalBerat: 0, categories: {}, items: [] });
  const [summaryKeluar, setSummaryKeluar] = useState({ totalBarang: 0, totalBerat: 0, categories: {}, items: [] });
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  // Role & Global Access
  const userRole = (localStorage.getItem('role') || '').toLowerCase().trim();
  const isGlobalRole = userRole === 'owner' || userRole === 'admin' || userRole === 'supervisor';
  const [selectedCabang, setSelectedCabang] = useState('');
  const [cabangOptions, setCabangOptions] = useState([]);

  // Modal State
  const [detailModal, setDetailModal] = useState({
    isOpen: false,
    title: '',
    type: 'masuk',
    summary: null,
  });

  DinamicTitle('Laporan Keluar - Masuk || Komaladewi');

  const tglAwal = moment(startDate).format('YYYY-MM-DD');
  const tglAkhir = moment(endDate).format('YYYY-MM-DD');

  // Fetch Cabang untuk Akun Global (Owner, Admin, Supervisor) - Khusus Cabang Etalase
  useEffect(() => {
    if (isGlobalRole) {
      async function fetchCabang() {
        try {
          const res = await http.get('/cabang', {
            headers: { Authorization: getAuthorizationHeader() },
          });
          const list = res.data?.data || (Array.isArray(res.data) ? res.data : []);
          const etalaseOnly = list.filter((c) => {
            const name = (c.nama_cabang || '').toLowerCase();
            const isEtalase = (c.is_toko === true || c.is_toko === 1 || String(c.is_toko) === 'true') || name.includes('etalase');
            return isEtalase && !name.includes('brankas');
          });
          setCabangOptions(etalaseOnly);
        } catch (err) {
          console.error('Error fetching cabang:', err);
        }
      }
      fetchCabang();
    }
  }, [isGlobalRole]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          tgl_awal: tglAwal,
          tgl_akhir: tglAkhir,
        });
        if (isGlobalRole && selectedCabang) {
          queryParams.append('cabang_id', selectedCabang);
        }
        const res = await http.get(`/laporan-keluar-masuk-operator?${queryParams.toString()}`, {
          headers: { Authorization: getAuthorizationHeader() },
          timeout: 15000,
        });

        if (res.data?.status) {
          setSummaryMasuk(res.data.data?.masuk || { totalBarang: 0, totalBerat: 0, categories: {}, items: [] });
          setSummaryKeluar(res.data.data?.keluar || { totalBarang: 0, totalBerat: 0, categories: {}, items: [] });
        }
      } catch (err) {
        console.error('Error fetching laporan', err);
        if (err.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [tglAwal, tglAkhir, selectedCabang, isGlobalRole, navigate]);

  const selectionRange = {
    startDate: startDate,
    endDate: endDate,
    key: 'selection',
  };

  function handleSelect(date) {
    setStartDate(date.selection.startDate);
    setEndDate(date.selection.endDate);
  }

  const handleOpenDetail = (type) => {
    const isMasuk = type === 'masuk';
    setDetailModal({
      isOpen: true,
      title: isMasuk ? 'Detail Barang Masuk' : 'Detail Barang Keluar (Pusat)',
      type,
      summary: isMasuk ? summaryMasuk : summaryKeluar,
    });
  };

  const handleCloseDetail = () => {
    setDetailModal({
      isOpen: false,
      title: '',
      type: 'masuk',
      summary: null,
    });
  };

  return (
    <Layouts title={isGlobalRole ? 'Laporan Keluar - Masuk' : 'Laporan Keluar - Masuk (Operator)'}>
      <div className="p-4 sm:p-6 bg-slate-50/50 dark:bg-slate-900/50 min-h-[70vh]">
        {/* Toolbar in Content */}
        <div className="flex items-center gap-2.5 mb-4 w-full flex-wrap bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm sticky top-0 z-40">
          <div className="dropdown dropdown-bottom">
            <div
              tabIndex={0}
              className="bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 p-2 rounded-lg cursor-pointer transition-colors shadow-sm flex items-center justify-center gap-2 px-4 text-sm font-medium border border-slate-200 dark:border-slate-600"
            >
              <IconCalendar size={18} className="text-amber-500" />
              <span>
                {moment(startDate).format('DD MMM YYYY')} - {moment(endDate).format('DD MMM YYYY')}
              </span>
            </div>
            <ul
              tabIndex={0}
              className="dropdown-content z-[99] menu p-2 shadow-2xl shadow-slate-200/50 dark:shadow-none bg-base-100 dark:bg-slate-800 rounded-xl mt-2 border border-slate-100 dark:border-slate-700 max-w-[92vw] overflow-x-auto"
            >
              <DateRange
                ranges={[selectionRange]}
                onChange={handleSelect}
                className="rounded-lg overflow-hidden border-none"
                rangeColors={['#f59e0b']}
              />
            </ul>
          </div>

          {/* Filter Toko / Cabang (Khusus Owner / Admin / Supervisor) */}
          {isGlobalRole && (
            <div className="flex items-center gap-2">
              <div className="relative min-w-[210px] sm:min-w-[260px]">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <IconBuildingStore size={18} className="text-amber-500" />
                </div>
                <select
                  value={selectedCabang}
                  onChange={(e) => setSelectedCabang(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 text-xs sm:text-sm bg-slate-50 dark:bg-slate-700/60 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 dark:focus:ring-amber-900/30 transition-all font-medium text-slate-700 dark:text-slate-200 cursor-pointer shadow-xs"
                >
                  <option value="">🌐 Seluruh Toko (Etalase)</option>
                  {cabangOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nama_cabang}
                    </option>
                  ))}
                </select>
              </div>
              {selectedCabang && (
                <button
                  type="button"
                  onClick={() => setSelectedCabang('')}
                  className="p-2 text-slate-400 hover:text-rose-500 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors shadow-xs"
                  title="Reset Filter Cabang (Tampilkan Seluruh Toko)"
                >
                  <IconX size={16} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Info Banner Filter Toko / Cabang */}
        {isGlobalRole && (
          <div className="max-w-5xl mx-auto mb-5 flex items-center justify-between flex-wrap gap-2 px-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200/70 dark:border-amber-800/60">
                <IconBuildingStore size={14} />
                {selectedCabang
                  ? `Filter Toko: ${cabangOptions.find((c) => String(c.id) === String(selectedCabang))?.nama_cabang || 'Cabang Terpilih'}`
                  : '🌐 Menampilkan Seluruh Toko (Etalase)'}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {selectedCabang
                  ? '(Data disaring khusus untuk cabang ini)'
                  : '(Semua transaksi barang masuk & keluar)'}
              </span>
            </div>
          </div>
        )}

        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
              <div className="w-10 h-10 rounded-full animate-spin border-4 border-dashed border-amber-500 border-t-transparent"></div>
              <p className="text-slate-500 text-sm font-medium animate-pulse">Memuat Laporan...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <RenderSummaryCard
                title="Barang Masuk"
                summary={summaryMasuk}
                type="masuk"
                onOpenDetail={handleOpenDetail}
              />
              <RenderSummaryCard
                title="Barang Keluar (Pusat)"
                summary={summaryKeluar}
                type="keluar"
                onOpenDetail={handleOpenDetail}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modal Detail & Ringkasan */}
      <DetailLaporanModal
        isOpen={detailModal.isOpen}
        onClose={handleCloseDetail}
        modalData={detailModal.summary}
        type={detailModal.type}
        startDate={startDate}
        endDate={endDate}
      />
    </Layouts>
  );
};

export default LaporanKeluarMasukOperator;
